import React, { useMemo } from "react";
import { Check, Clock3, AlertTriangle } from "lucide-react";

/**
 * 5-step focused tracker — Mortgage Offer → Searches → Enquiries → Signed Contracts → Exchange.
 * Maps from canonical 12-stage pipeline.
 */
const PATH = [
  { id: "mortgage_offer", label: "Mortgage Offer",   match: ["mortgage_app", "mortgage_offer"] },
  { id: "searches",       label: "Searches",         match: ["searches", "survey"] },
  { id: "enquiries",      label: "Enquiries",        match: ["enquiries"] },
  { id: "exchange_ready", label: "Signed Contracts", match: ["exchange_ready"] },
  { id: "exchanged",      label: "Exchange",         match: ["exchanged", "completed"] },
];

const STAGE_ORDER = [
  "offer", "aml", "memo", "solicitors", "mortgage_app", "survey", "searches",
  "enquiries", "mortgage_offer", "exchange_ready", "exchanged", "completed",
];

const _ms = (v) => (typeof v === "number" ? v : Date.parse(v || ""));

export const CriticalPathToExchange = ({ deal, openTasks = [] }) => {
  const dealStageIdx = STAGE_ORDER.indexOf(deal.stage);

  const steps = useMemo(() => {
    const now = Date.now();
    return PATH.map((step) => {
      // A step is "completed" if the deal stage is past the LAST canonical match.
      const lastMatchIdx = Math.max(...step.match.map((m) => STAGE_ORDER.indexOf(m)));
      const completed = dealStageIdx > lastMatchIdx;
      const current = step.match.includes(deal.stage);

      // Step is "blocked" if any open task with kickoff_stage_hint pointing to this step is overdue.
      const blocked = openTasks.some((t) => {
        if (!t.due_at || _ms(t.due_at) >= now) return false;
        const hint = t.kickoff_stage_hint;
        return hint && step.match.includes(hint);
      });

      let state = "upcoming";
      if (completed) state = "completed";
      else if (blocked) state = "blocked";
      else if (current) state = "current";

      return { ...step, state };
    });
  }, [deal.stage, dealStageIdx, openTasks]);

  return (
    <section
      className="rounded-2xl bg-white border border-slate-200 p-5 lg:p-6"
      data-testid="critical-path-to-exchange"
    >
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">Operational</p>
          <h3 className="font-display text-[18px] font-bold tracking-tight text-slate-900 mt-0.5">Critical path to exchange</h3>
        </div>
        <p className="text-[11px] text-slate-500">5 stages · drag-tested progression</p>
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Connector */}
        <span aria-hidden="true" className="absolute left-0 right-0 top-[18px] h-[2px] bg-slate-100" />
        <div className="relative grid grid-cols-5 gap-1">
          {steps.map((s, i) => {
            const isCompleted = s.state === "completed";
            const isCurrent   = s.state === "current";
            const isBlocked   = s.state === "blocked";

            const ringCls = isCompleted
              ? "bg-emerald-500 text-white border-emerald-500"
              : isBlocked
              ? "bg-rose-500 text-white border-rose-500 cf-pulse"
              : isCurrent
              ? "bg-indigo-500 text-white border-indigo-500"
              : "bg-white text-slate-400 border-slate-200";

            const labelCls = isCompleted
              ? "text-emerald-700"
              : isBlocked
              ? "text-rose-700 font-bold"
              : isCurrent
              ? "text-indigo-800 font-bold"
              : "text-slate-500";

            return (
              <div
                key={s.id}
                className="flex flex-col items-center text-center"
                data-testid={`critical-path-step-${s.id}`}
                data-state={s.state}
              >
                <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${ringCls}`}>
                  {isCompleted ? <Check size={13} strokeWidth={3} />
                    : isBlocked  ? <AlertTriangle size={13} />
                    : isCurrent  ? <span className="text-[12px] font-bold cf-num">{i + 1}</span>
                    : <span className="text-[12px] font-bold cf-num">{i + 1}</span>}
                </div>
                <p className={`mt-2 text-[10.5px] uppercase tracking-[0.10em] leading-tight ${labelCls}`}>
                  {s.label}
                </p>
                {isBlocked && (
                  <span className="mt-1 inline-flex items-center gap-1 text-[9.5px] text-rose-700 font-bold uppercase tracking-[0.12em]">
                    <AlertTriangle size={9} /> Blocked
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CriticalPathToExchange;
